import { DEMO } from '../services/sdk'

/**
 * The documentation page's code samples and its error table.
 *
 * Held as data rather than written into the page so that the Persian prose and
 * the Latin code stay separable: the prose is translated and audited, the code
 * is copied into somebody's editor and must not be touched by either.
 *
 * Every credential here is fake and visibly fake. A documentation page whose
 * example runs as written is a page that bills somebody for reading it.
 */

export const INSTALL = [
  `pip install --index-url ${DEMO.index} ${DEMO.package}`,
  '',
  '# Python 3.11+. httpx is the only runtime dependency.',
].join('\n')

export const SYNC_EXAMPLE = [
  'from api_platform import Client',
  '',
  'client = Client(',
  `    client_id="${DEMO.clientId}",`,
  `    client_secret="${DEMO.clientSecret}",`,
  `    base_url="${DEMO.baseUrl}",`,
  ')',
  '',
  'with client:',
  '    balance = client.wallet()',
  '    print(balance.available_balance_irr)  # integer rials',
  '',
  '    result = client.banking.card_to_iban(',
  '        card_number="0000000000000000",',
  '        idempotency_key="order-4711",',
  '    )',
  '    print(result.iban)',
].join('\n')

export const ASYNC_EXAMPLE = [
  'import asyncio',
  '',
  'from api_platform import AsyncClient',
  '',
  '',
  'async def main() -> None:',
  '    async with AsyncClient(',
  `        client_id="${DEMO.clientId}",`,
  `        client_secret="${DEMO.clientSecret}",`,
  `        base_url="${DEMO.baseUrl}",`,
  '    ) as client:',
  '        result = await client.identity.mobile_match(',
  '            national_code="0000000000",',
  '            mobile="09000000000",',
  '            idempotency_key="signup-4711",',
  '        )',
  '        print(result.matched)',
  '',
  '',
  'asyncio.run(main())',
].join('\n')

export const ERROR_EXAMPLE = [
  'from api_platform import (',
  '    ApiPlatformError,',
  '    InsufficientFundsError,',
  '    PermissionDeniedError,',
  '    ProviderTimeoutError,',
  ')',
  '',
  'try:',
  '    result = client.banking.iban_info(',
  '        iban="IR000000000000000000000000",',
  '        idempotency_key="order-4711",',
  '    )',
  'except InsufficientFundsError:',
  '    ...  # top up the wallet, then retry',
  'except PermissionDeniedError:',
  '    ...  # this application was not granted the operation',
  'except ProviderTimeoutError:',
  '    ...  # unknown outcome: retry only with the SAME idempotency key',
  'except ApiPlatformError as exc:',
  '    log.error("failed [%s] request=%s", exc.code, exc.request_id)',
].join('\n')

export const IDEMPOTENCY_EXAMPLE = [
  'def order_key(order_id: str) -> str:',
  '    """Derived from your own unit of work, so it survives a restart."""',
  '    return f"order:{order_id}:card-to-iban"',
  '',
  '',
  'result = client.banking.card_to_iban(',
  '    card_number="0000000000000000",',
  '    idempotency_key=order_key("4711"),',
  ')',
  '',
  '# Your own correlation value travels as X-Request-ID and joins the call to',
  '# your logs. The platform mints its own id and returns that one; quote the',
  "# platform's id to support.",
  'raw = client.request(',
  '    "GET",',
  '    "/api/v1/wallet/",',
  '    request_id="trace-4711-attempt-1",',
  ')',
].join('\n')

export interface ErrorEntry {
  code: string
  meaning: string
  action: string
}

/**
 * The public error taxonomy.
 *
 * Provider-neutral by design: no upstream's own status codes reach a customer,
 * and none of these names identifies who serves a capability. What each one
 * answers is the only question a caller has at the moment it arrives — should I
 * retry this, and was I charged.
 */
/**
 * The Persian reading of one public error code.
 *
 * The server's own message is written in English for a developer reading a
 * stack trace; a customer looking at the Sandbox needs the same fact in the
 * language the rest of the screen is in. The code stays visible either way —
 * it is the part support and the SDK both quote.
 */
export function errorEntry(code: string): ErrorEntry | undefined {
  return ERRORS.find((entry) => entry.code === code)
}

export const ERRORS: readonly ErrorEntry[] = [
  {
    code: 'permission_denied',
    meaning: 'این اپلیکیشن اجازهٔ فراخوانی این سرویس را ندارد.',
    action: 'دسترسی از سمت ما داده می‌شود. با ما تماس بگیرید؛ تکرار درخواست کمکی نمی‌کند.',
  },
  {
    code: 'insufficient_funds',
    meaning: 'موجودی قابل استفادهٔ کیف پول برای بهای این فراخوانی کافی نبود.',
    action: 'اعتبار را افزایش دهید و سپس دوباره تلاش کنید. مبلغی برداشت نشده است.',
  },
  {
    code: 'idempotency_conflict',
    meaning: 'همین کلید یکتایی پیش‌تر با محتوای دیگری به کار رفته است.',
    action: 'برای پرسش تازه کلید تازه بسازید. سامانه پاسخ پرسشی را که نپرسیده‌اید نمی‌دهد.',
  },
  {
    code: 'rate_limited',
    meaning: 'تعداد درخواست‌ها از حد مجاز گذشته است.',
    action: 'به بازهٔ اعلام‌شده احترام بگذارید و پس از آن دوباره تلاش کنید.',
  },
  {
    code: 'provider_rejected',
    meaning: 'درخواست بررسی و رد شد. این یک پاسخ است، نه خرابی سامانه.',
    action: 'تکرار همین درخواست پاسخ تازه‌ای نمی‌دهد. ورودی‌ها را بازبینی کنید.',
  },
  {
    code: 'provider_unavailable',
    meaning: 'سرویس در دسترس نبود و درخواست ارسال نشد.',
    action: 'مبلغی برداشت نشده است. کمی بعد دوباره تلاش کنید.',
  },
  {
    code: 'provider_timeout',
    meaning: 'پاسخی در زمان مقرر دریافت نشد. ممکن است کار انجام شده باشد.',
    action: 'تنها با همان کلید یکتایی دوباره تلاش کنید تا دوباره هزینه نگیرید.',
  },
  {
    code: 'operation_unavailable',
    meaning: 'این سرویس در حال حاضر قابل اجرا نیست.',
    action: 'تکرار درخواست کمکی نمی‌کند. با پشتیبانی تماس بگیرید.',
  },
  {
    code: 'configuration_error',
    meaning: 'اشکال از سمت ماست، نه از درخواست شما.',
    action: 'تکرار درخواست کمکی نمی‌کند. کد پیگیری را برای پشتیبانی بفرستید.',
  },
]
